import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { AuthService } from 'src/app/services/auth.service';
import { ErrorService } from 'src/app/core/services/error.service';
import { CustomAlertService } from 'src/app/core/services/custom-alert.service';
import { ProfileCacheService } from 'src/app/core/services/profile-cache.service';
import Swal from 'sweetalert2';

/** A dónde se entra cuando no hay un `returnUrl` válido. */
const HOME_URL = '/gasprecio/monitor';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit {

    loginForm: UntypedFormGroup;
    loading = false;
    submitted = false;
    error = '';
    showPassword = false;

    @ViewChild('emailInput') emailInput: ElementRef<HTMLInputElement>;
    @ViewChild('passwordInput') passwordInput: ElementRef<HTMLInputElement>;

    constructor(
        private formBuilder: UntypedFormBuilder,
        private router: Router,
        private route: ActivatedRoute,
        private authenticationService: AuthService,
        private errorService: ErrorService,
        private customAlert: CustomAlertService,
        private profileCacheService: ProfileCacheService
    ) {
        // Sesión vigente: no hay nada que pedir, directo al portal.
        if (this.authenticationService.token) {
            this.router.navigateByUrl(this.targetUrl());
        }
    }

    ngOnInit() {
        this.loginForm = this.formBuilder.group({
            email: ['', [Validators.required, Validators.email]],
            password: ['', Validators.required]
        });

        this.tryAutoLoginFromQuery();
    }

    get f() { return this.loginForm.controls; }

    /** Alimenta la palomita del campo de correo: significa "válido", no "tiene texto". */
    get emailValid() { return this.f.email.valid; }

    onSubmit() {
        if (this.loading) {
            return;
        }

        this.readAutofilledValues();
        this.submitted = true;

        if (this.loginForm.invalid) {
            return;
        }

        this.loading = true;

        // Limpiar cachés antes de hacer login
        this.profileCacheService.clearCache();

        const email = this.f.email.value;
        const password = this.f.password.value;

        this.authenticationService.login(email, password)
            .subscribe({
                next: (data: any) => {
                    if (!data?.user?.stations?.length) {
                        this.loading = false;
                        this.customAlert.showError('Tu cuenta no tiene estaciones asignadas', 'Escríbenos a hola@gaspre.com para activarla.');
                        return;
                    }

                    this.authenticationService.setSession(data, email, password);
                    this.enterPortal();
                }, error: (err) => {
                    this.loading = false;
                    // delegate to error service; if it's a validation object it will reject with details
                    this.errorService.handle(err).catch(() => {
                        // fallback: show generic invalid credentials
                        this.customAlert.showError('El usuario o la contraseña son inválidos', 'Inténtalo de nuevo');
                    });
                }
            });
    }

    /**
     * Entra al portal con el router, sin recargar el documento.
     *
     * Antes se hacía `window.location.reload()` sobre /login: el bundle completo
     * volvía a arrancar, se veía otra vez el formulario de login un instante, y
     * recién entonces el constructor redirigía al monitor. Hacía falta porque el
     * interceptor y AuthService capturaban el token en su constructor; ahora lo
     * leen fresco en cada petición, así que basta con navegar. `loading` se queda
     * en true: el botón muestra "Entrando…" hasta que el portal reemplaza esta
     * vista (con los módulos precargados eso es casi inmediato).
     */
    private enterPortal(): void {
        this.router.navigateByUrl(this.targetUrl()).then(navigated => {
            if (!navigated) {
                // Un guard rechazó la ruta: se devuelve el formulario en vez de
                // dejar el botón girando.
                this.loading = false;
            }
        }).catch(() => {
            this.loading = false;
        });
    }

    /**
     * `returnUrl` lo pone AuthGuard cuando saca al usuario de una ruta protegida.
     * Solo se aceptan rutas internas (empiezan con una sola `/`) para no servir
     * de redirección abierta, y nunca el propio login.
     */
    private targetUrl(): string {
        const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl') ?? '';
        const isInternal = returnUrl.startsWith('/') && !returnUrl.startsWith('//');
        if (!isInternal || returnUrl.startsWith('/login')) {
            return HOME_URL;
        }
        return returnUrl;
    }

    /**
     * Copia al formulario lo que el navegador autocompletó en los campos.
     *
     * Chrome y Safari llenan correo y contraseña al cargar la página sin disparar
     * el evento `input`, así que el `loginForm` sigue vacío aunque el usuario vea
     * sus datos puestos. Sin esto el primer click no autenticaba y había que dar
     * un segundo click (reporte "hay que dar 2 click" al entrar a GasPre).
     */
    private readAutofilledValues(): void {
        const email = this.emailInput?.nativeElement.value ?? '';
        const password = this.passwordInput?.nativeElement.value ?? '';

        if (email && email !== this.f.email.value) {
            this.f.email.setValue(email);
        }
        if (password && password !== this.f.password.value) {
            this.f.password.setValue(password);
        }
    }

    togglePasswordVisibility() {
        this.showPassword = !this.showPassword;
    }

    // kept for compatibility but now handled by ErrorService / CustomAlertService
    errorLogin() {
        this.customAlert.showError('El usuario o la contraseña son inválidos', 'Inténtalo de nuevo');
    }

    async recoverPassword() {
        const { value: email } = await Swal.fire({
            title: 'Recuperar contraseña',
            text: 'Ingrese su cuenta de correo electrónico y le enviaremos instrucciones para recuperar su contraseña.',
            input: 'email',
            inputPlaceholder: 'Correo electrónico',
            confirmButtonText: 'Enviar',
            confirmButtonColor: '#00e07b'
        });

        if (email) {
            await this.authenticationService.sendRecoverPassword(email);
            Swal.fire({
                title: 'Correo enviado',
                text: `Te hemos enviado un correo a ${email} con las instrucciones para recuperar tu contraseña.`,
                icon: 'success',
                confirmButtonColor: '#00e07b',
                confirmButtonText: 'Aceptar'
            });
        }
    }

    private tryAutoLoginFromQuery(): void {
        const code = this.route.snapshot.queryParamMap.get('code');
        if (!code) return;

        localStorage.clear();

        try {
            // decode base64 → objeto
            const decoded = atob(code);
            const response = JSON.parse(decoded);

            localStorage.setItem('token', response?.user?.token);
            localStorage.setItem('idStation', response?.user?.stations[0]);
            localStorage.setItem('user', JSON.stringify(response.user));

            this.router.navigate([HOME_URL]);

        } catch (err) {
            console.error('Auto-login por query param falló:', err);
            // Si falla, se queda el form de login normal
        }
    }
}
